import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert, Box, Button, Chip, CircularProgress, Container, IconButton, Paper, ToggleButton, ToggleButtonGroup, Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { AppHelper } from "../../AppHelper";
import AppTopBar from "../common/app-top-bar";
import { isGameSkill, skillLabel } from "../../constants/game-skills";

const MAX_PHOTOS = 3;
const MAX_FILE_BYTES = 20 * 1024 * 1024;
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];
const LONGEST_SIDE = 1600;
const GRADES = ["P3", "P4", "P5", "P6"];

const STATUS = {
  needs_help: { label: "Let's work on this", color: "warning" },
  correct: { label: "Correct", color: "success" },
  incorrect: { label: "Check this one", color: "error" },
  marked_wrong: { label: "Marked wrong", color: "error" },
  marked_right: { label: "Marked right", color: "success" },
};
const KIND = {
  not_started: "Homework to do",
  answered: "Answers to check",
  marked: "Marked by your teacher",
  mixed: "Homework",
  unclear: "Homework",
};

// Shrinks a photo in the browser first: a smaller upload, and the re-drawn picture carries no camera metadata.
function shrink(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, LONGEST_SIDE / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      const dataUrl = canvas.toDataURL("image/jpeg", 0.82);
      resolve({ dataUrl, b64: dataUrl.split(",")[1] });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("unreadable"));
    };
    img.src = url;
  });
}

export default function PageHomeworkHelp() {
  const navigate = useNavigate();
  const oUser = AppHelper.userCacheGet();
  const refTopBar = useRef(null);
  const cameraInput = useRef(null);
  const fileInput = useRef(null);

  const [photos, setPhotos] = useState([]);
  const [grade, setGrade] = useState(oUser && GRADES.includes(oUser.grade) ? oUser.grade : "P5");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [quota, setQuota] = useState(null);

  useEffect(() => {
    if (!oUser) navigate("/login");
  }, []);

  if (!oUser) return null;

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    setError("");
    const room = MAX_PHOTOS - photos.length;
    if (files.length > room) setError(`You can add up to ${MAX_PHOTOS} photos.`);
    const added = [];
    for (const file of files.slice(0, Math.max(room, 0))) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        setError("Please use JPEG, PNG or WebP photos.");
        continue;
      }
      if (file.size > MAX_FILE_BYTES) {
        setError("One of the photos is too large. Try a smaller photo.");
        continue;
      }
      try {
        added.push({ id: `${Date.now()}-${Math.random()}`, ...(await shrink(file)) });
      } catch (err) {
        setError("One of the photos could not be read.");
      }
    }
    if (added.length) setPhotos((p) => [...p, ...added]);
  };

  const removePhoto = (id) => setPhotos((p) => p.filter((x) => x.id !== id));

  const onSubmit = async () => {
    if (busy || photos.length === 0) return;
    setBusy(true);
    setError("");
    const res = await AppHelper.apiPost("api/homework/help", { token: oUser.token, images: photos.map((p) => p.b64), grade });
    setBusy(false);
    if (!res) {
      setError("We could not reach the server. Check your internet and try again.");
      return;
    }
    if (!res.status) {
      setError(res.message || "Something went wrong. Please try again.");
      return;
    }
    setPhotos([]);
    setResult(res.data);
    if (res.quota) setQuota(res.quota);
  };

  const onReset = () => {
    setResult(null);
    setError("");
  };

  const showForm = !result;

  return (
    <div>
      <AppTopBar ref={refTopBar} />
      <Container component="main" maxWidth="sm" sx={{ mb: 4 }}>
        <Paper
          elevation={0}
          sx={{ my: { xs: 3, md: 5 }, p: { xs: 2, md: 3 }, border: "3px solid #25204A", boxShadow: "0 8px 0 #25204A", borderRadius: "28px" }}
        >
          <Typography variant="h5" sx={{ fontWeight: "bold", marginBottom: 1 }}>
            <span aria-hidden="true">📸 </span>Help with my homework
          </Typography>

          {showForm && (
            <>
              <Typography sx={{ marginBottom: 1 }}>
                Add up to {MAX_PHOTOS} photos of your homework. It can be work you have not started, work you have filled in, or work your teacher has marked.
              </Typography>
              <Typography variant="body2" sx={{ marginBottom: 2, color: "#5b6b7c" }}>
                We do not save your photos. Please keep faces and names out of the picture.
              </Typography>

              <Box display="flex" alignItems="center" gap={1} sx={{ marginBottom: 2 }}>
                <Typography variant="body2">Level:</Typography>
                <ToggleButtonGroup size="small" exclusive color="success" value={grade} onChange={(e, g) => g && setGrade(g)}>
                  {GRADES.map((g) => (
                    <ToggleButton key={g} value={g} disabled={busy}>{g}</ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>

              <input ref={cameraInput} type="file" accept="image/*" capture="environment" hidden onChange={onFiles} />
              <input ref={fileInput} type="file" accept="image/jpeg,image/png,image/webp" multiple hidden onChange={onFiles} />
              <Box display="flex" gap={1} flexWrap="wrap" sx={{ marginBottom: 2 }}>
                <Button variant="contained" color="secondary" disabled={busy || photos.length >= MAX_PHOTOS} onClick={() => cameraInput.current.click()}>
                  Take a photo
                </Button>
                <Button variant="outlined" disabled={busy || photos.length >= MAX_PHOTOS} onClick={() => fileInput.current.click()}>
                  Choose photos
                </Button>
              </Box>

              {photos.length > 0 && (
                <Box display="flex" gap={1} flexWrap="wrap" sx={{ marginBottom: 2 }}>
                  {photos.map((p, i) => (
                    <Box key={p.id} sx={{ position: "relative", width: 110, height: 110, border: "3px solid #25204A", borderRadius: "16px", overflow: "hidden" }}>
                      <img src={p.dataUrl} alt={`Homework photo ${i + 1}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      <IconButton
                        size="small"
                        aria-label={`Remove photo ${i + 1}`}
                        disabled={busy}
                        onClick={() => removePhoto(p.id)}
                        sx={{ position: "absolute", top: 2, right: 2, backgroundColor: "#fff", "&:hover": { backgroundColor: "#fff" } }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}

              {error && <Alert severity="warning" sx={{ marginBottom: 2 }}>{error}</Alert>}

              <Button variant="contained" color="success" size="large" disabled={busy || photos.length === 0} onClick={onSubmit}>
                {busy ? "Reading your homework..." : "Help me with this"}
              </Button>
              {busy && (
                <Box display="flex" alignItems="center" gap={2} sx={{ marginTop: 2 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2">This can take up to a minute.</Typography>
                </Box>
              )}
            </>
          )}

          {result && result.refused && (
            <>
              <Alert severity="info" sx={{ marginBottom: 2 }}>{result.message}</Alert>
              <Button variant="contained" color="success" onClick={onReset}>Try again</Button>
            </>
          )}

          {result && !result.refused && (
            <>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ marginBottom: 1 }}>
                <Chip label={KIND[result.kind] || "Homework"} color="secondary" />
                {result.subject && <Chip label={result.subject} variant="outlined" />}
              </Box>
              {result.summary && <Typography sx={{ marginBottom: 2 }}>{result.summary}</Typography>}

              {result.items.map((it, i) => {
                const st = STATUS[it.status] || STATUS.needs_help;
                return (
                  <Paper key={i} variant="outlined" sx={{ padding: 1.5, marginBottom: 1.5, border: "2px solid #25204A", borderRadius: "16px" }}>
                    <Box display="flex" alignItems="center" gap={1} sx={{ marginBottom: 0.5 }}>
                      {it.ref && <Typography sx={{ fontWeight: 800 }}>{it.ref}</Typography>}
                      <Chip size="small" label={st.label} color={st.color} />
                    </Box>
                    <Typography>{it.help}</Typography>
                  </Paper>
                );
              })}

              {result.tips.length > 0 && (
                <Paper elevation={0} sx={{ padding: 1.5, marginBottom: 2, backgroundColor: "#FFF1BF", border: "2px solid #25204A", borderRadius: "16px" }}>
                  <Typography sx={{ fontWeight: 800, marginBottom: 0.5 }}><span aria-hidden="true">💡 </span>Tips</Typography>
                  {result.tips.map((t, i) => (
                    <Typography key={i} sx={{ marginBottom: 0.5 }}>{t}</Typography>
                  ))}
                </Paper>
              )}

              <Box display="flex" gap={1} flexWrap="wrap">
                {result.practise && isGameSkill(result.practise) && (
                  <Button variant="contained" color="secondary" onClick={() => navigate(`/game?skill=${result.practise}&grade=${grade}`)}>
                    Practise {skillLabel(result.practise).toLowerCase()}
                  </Button>
                )}
                <Button variant="contained" color="success" onClick={onReset}>Help with something else</Button>
              </Box>
              {quota && (
                <Typography variant="body2" sx={{ marginTop: 2, color: "#5b6b7c" }}>
                  {quota.remaining} homework {quota.remaining === 1 ? "help" : "helps"} left today.
                </Typography>
              )}
              <Typography variant="body2" sx={{ marginTop: 1, color: "#5b6b7c" }}>
                This help is written by a computer and can make mistakes. Check with your teacher if you are unsure.
              </Typography>
            </>
          )}
        </Paper>
      </Container>
    </div>
  );
}
